<script setup>
/**
 * The page behind an Agent KPI card.
 *
 * Agent KPI answers "who needs help today" in one screen. This answers "why is
 * that number what it is" for one metric: the same headline figure the card
 * showed, how it was built, and the cases it was built from.
 *
 * One component serves every topic — the route supplies `meta.qaTopic` and the
 * table below reads from the same incident queue the Live Monitor uses, so a
 * new topic is a config entry rather than a new page.
 */
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Avatar from 'next/avatar/Avatar.vue';
import QaDateRangeSelect from '../components/QaDateRangeSelect.vue';
import QaDashboardApi from 'dashboard/api/qaDashboard';
import '../styles/qaShell.css';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const showAlert = useAlert;
const { accountScopedRoute } = useAccount();

const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_WINDOW_DAYS = 90;
const PER_PAGE = 25;

const toDateInput = date => {
  const d = new Date(date);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Each topic names the queue it draws from and the figure it explains. Adding
// "first reply" or "overdue" later is an entry here, not another page.
const TOPICS = {
  template_compliance: {
    // Drawn from the incident queue: a template miss raises one.
    source: 'incidents',
    sourceType: 'template_audit',
    titleKey: 'QA_DASHBOARD.AGENT_KPI.TEMPLATE_COMPLIANCE',
    introKey: 'QA_DASHBOARD.METRIC_DETAIL.TEMPLATE_INTRO',
    rate: payload => payload.quality?.template_compliance?.rate,
    numerator: payload => payload.quality?.template_compliance?.correct,
    denominator: payload =>
      payload.quality?.template_compliance?.eligible_finalized,
    basisKey: 'QA_DASHBOARD.METRIC_DETAIL.BASIS_AUDITED',
    higherIsBetter: true,
    tabs: [
      {
        key: 'all',
        labelKey: 'QA_DASHBOARD.LIVE_MONITOR.TAB_ALL',
        stat: 'all',
      },
      {
        key: 'incorrect',
        labelKey: 'QA_DASHBOARD.LIVE_MONITOR.TAB_INCORRECT',
        stat: 'incorrect',
      },
      {
        key: 'no_reply',
        labelKey: 'QA_DASHBOARD.LIVE_MONITOR.TAB_NO_REPLY',
        stat: 'no_reply',
      },
      {
        key: 'confirmed',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.TAB_CONFIRMED',
        stat: 'confirmed',
      },
      {
        key: 'passed',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.TAB_PASSED',
        stat: 'passed',
      },
    ],
    steps: ({ stats, numerator }) => [
      {
        key: 'detected',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.STEP_DETECTED',
        value: stats.all,
      },
      {
        key: 'correct',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.STEP_CORRECT',
        value: numerator,
      },
      {
        key: 'confirmed',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.STEP_CONFIRMED',
        value: stats.confirmed,
      },
      {
        key: 'unreviewed',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.STEP_UNREVIEWED',
        value: Math.max(
          Number(stats.all || 0) - Number(stats.reviewed || 0),
          0
        ),
      },
    ],
  },
  confirmed_safety: {
    // One safety detection raises exactly one incident, so the queue holds the
    // same rows the card counts — verified against qa_safety_incidents before
    // this page was pointed at it.
    source: 'incidents',
    sourceType: 'safety_language',
    // A count, not a rate: there is no denominator to be a share of, and
    // inventing one ("3 of how many messages?") would be a different claim.
    kind: 'count',
    titleKey: 'QA_DASHBOARD.AGENT_KPI.SAFETY_CONFIRMED',
    introKey: 'QA_DASHBOARD.METRIC_DETAIL.SAFETY_INTRO',
    count: payload => payload.quality?.safety_confirmed_violations,
    pending: payload => payload.quality?.safety_pending_machine_flags,
    higherIsBetter: false,
    tabs: [
      {
        key: 'all',
        labelKey: 'QA_DASHBOARD.LIVE_MONITOR.TAB_ALL',
        stat: 'all',
      },
      {
        key: 'confirmed',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.TAB_CONFIRMED',
        stat: 'confirmed',
      },
      {
        key: 'passed',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.TAB_PASSED',
        stat: 'passed',
      },
      {
        key: 'needs_review',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.TAB_NEEDS_REVIEW',
        stat: 'needs_review',
      },
    ],
    steps: ({ stats }) => [
      {
        key: 'detected',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.STEP_FLAGGED',
        value: stats.all,
      },
      {
        key: 'confirmed',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.STEP_CONFIRMED_SAFETY',
        value: stats.confirmed,
      },
      {
        key: 'passed',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.STEP_CLEARED',
        value: stats.passed,
      },
      {
        key: 'awaiting',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.STEP_AWAITING',
        value: Math.max(
          Number(stats.all || 0) - Number(stats.reviewed || 0),
          0
        ),
      },
    ],
  },
  first_reply: {
    // No incident is raised for a reply that merely ran late, so these cases
    // live in the SLA episodes and nowhere else.
    source: 'episodes',
    titleKey: 'QA_DASHBOARD.AGENT_KPI.FRT_RATE',
    introKey: 'QA_DASHBOARD.METRIC_DETAIL.FIRST_REPLY_INTRO',
    rate: payload => payload.first_human_response?.rate,
    numerator: payload => payload.first_human_response?.within_sla_count,
    denominator: payload => payload.first_human_response?.eligible_count,
    basisKey: 'QA_DASHBOARD.METRIC_DETAIL.BASIS_REPLIES',
    higherIsBetter: true,
    tabs: [
      {
        key: 'all',
        labelKey: 'QA_DASHBOARD.LIVE_MONITOR.TAB_ALL',
        stat: 'all',
      },
      {
        key: 'late',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.TAB_LATE',
        stat: 'late',
      },
      {
        key: 'on_time',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.TAB_ON_TIME',
        stat: 'on_time',
      },
      {
        key: 'unanswered',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.TAB_UNANSWERED',
        stat: 'unanswered',
      },
    ],
    steps: ({ stats }) => [
      {
        key: 'claimed',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.STEP_CLAIMED',
        value: stats.all,
      },
      {
        key: 'answered',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.STEP_ELIGIBLE',
        value: stats.eligible,
      },
      {
        key: 'late',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.STEP_LATE',
        value: stats.late,
      },
      {
        key: 'unanswered',
        labelKey: 'QA_DASHBOARD.METRIC_DETAIL.STEP_UNANSWERED',
        value: stats.unanswered,
      },
    ],
  },
};

const topic = computed(
  () => TOPICS[route.meta?.qaTopic] || TOPICS.template_compliance
);

const filters = reactive({
  window_start: toDateInput(Date.now() - 6 * DAY_MS),
  window_end: toDateInput(Date.now()),
  team_id: '',
  inbox_id: '',
  agent_user_id: '',
});

const isLoading = ref(false);
const payload = ref({});
const previousPayload = ref(null);
const rows = ref([]);
const stats = reactive({});
const activeTab = ref('all');
const page = ref(1);
const totalPages = ref(1);
const totalCount = ref(0);

const agents = computed(() => store.getters['agents/getAgents'] || []);
const agentName = id => {
  const agent = agents.value.find(a => Number(a.id) === Number(id));
  if (!agent) return id ? `#${id}` : '—';
  return agent.name || agent.available_name || agent.email || `#${id}`;
};
const agentThumbnail = id =>
  agents.value.find(a => Number(a.id) === Number(id))?.thumbnail || '';

const pct = value => {
  if (value === null || value === undefined || value === '') return '—';
  return `${(Number(value) * 100).toFixed(1)}%`;
};

const num = value => {
  if (value === null || value === undefined || value === '') return '—';
  return Number(value);
};

const formatTime = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
};

const fmtSeconds = value => {
  if (value === null || value === undefined) return '—';
  const seconds = Number(value);
  if (!Number.isFinite(seconds)) return '—';
  return seconds < 60
    ? `${seconds.toFixed(1)}s`
    : `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
};

// An episode with no reply is not "late" — nobody answered at all, and that is
// a different failure worth its own word and its own colour.
const verdictLabelKey = row => {
  if (row.waited_seconds === null || row.waited_seconds === undefined) {
    return 'QA_DASHBOARD.METRIC_DETAIL.VERDICT_UNANSWERED';
  }
  return row.on_time
    ? 'QA_DASHBOARD.METRIC_DETAIL.VERDICT_ON_TIME'
    : 'QA_DASHBOARD.METRIC_DETAIL.VERDICT_LATE';
};

const verdictTone = row => {
  if (row.waited_seconds === null || row.waited_seconds === undefined) {
    return 'qa-pill--bad';
  }
  return row.on_time ? 'qa-pill--muted' : 'qa-pill--warn';
};

// Some topics are a rate of something; a safety count is not. Both still have to
// answer "what is the figure, and what is it built on".
const isCountTopic = computed(() => topic.value.kind === 'count');

const rate = computed(() =>
  isCountTopic.value ? null : topic.value.rate(payload.value || {})
);
const countValue = computed(() =>
  isCountTopic.value ? topic.value.count(payload.value || {}) : null
);
const numerator = computed(() =>
  isCountTopic.value ? null : topic.value.numerator(payload.value || {})
);
const denominator = computed(() =>
  isCountTopic.value ? null : topic.value.denominator(payload.value || {})
);

const isScored = computed(() =>
  isCountTopic.value
    ? countValue.value !== null && countValue.value !== undefined
    : Number(denominator.value) > 0
);

// Same rule as the card: an earlier window that measured nothing is not a
// previous value of zero, so it earns no comparison.
const delta = computed(() => {
  if (!previousPayload.value || !isScored.value) return null;

  if (isCountTopic.value) {
    const before = topic.value.count(previousPayload.value);
    if (before === null || before === undefined) return null;
    // Nothing this window and nothing last window is not "no change" — there was
    // nothing to change. Saying so implies a comparison nobody made.
    if (!Number(countValue.value) && !Number(before)) return null;

    const diff = Number(countValue.value) - Number(before);
    return { diff, better: topic.value.higherIsBetter ? diff > 0 : diff < 0 };
  }

  const before = topic.value.rate(previousPayload.value);
  const basis = topic.value.denominator(previousPayload.value);
  if (!(Number(basis) > 0) || before === null || before === undefined) {
    return null;
  }
  const diff = (Number(rate.value) - Number(before)) * 100;
  return {
    diff,
    better: topic.value.higherIsBetter ? diff > 0 : diff < 0,
  };
});

const deltaText = computed(() => {
  if (!delta.value) return '';
  if (Math.abs(delta.value.diff) < 0.05) {
    return t('QA_DASHBOARD.AGENT_KPI.DELTA_FLAT');
  }
  return t('QA_DASHBOARD.AGENT_KPI.DELTA', {
    arrow: delta.value.diff > 0 ? '▲' : '▼',
    value: Math.abs(delta.value.diff).toFixed(1),
  });
});

const deltaClass = computed(() => {
  if (!delta.value || Math.abs(delta.value.diff) < 0.05) return 'qa-delta';
  return delta.value.better
    ? 'qa-delta qa-delta--up'
    : 'qa-delta qa-delta--down';
});

const windowDays = computed(() => {
  const start = new Date(`${filters.window_start}T00:00:00`);
  const end = new Date(`${filters.window_end}T00:00:00`);
  const days = Math.round((end - start) / DAY_MS) + 1;
  return Number.isFinite(days) && days > 0 ? days : 1;
});

// How the headline was built, in the order the reader would ask it.
const breakdown = computed(() =>
  topic.value
    .steps({
      stats,
      numerator: numerator.value,
      denominator: denominator.value,
    })
    .map(step => ({ ...step, value: num(step.value) }))
);

// Outcome filters for this topic, each carrying the count it will open.
const TABS = computed(() =>
  topic.value.tabs.map(tab => ({ ...tab, count: stats[tab.stat] }))
);

const backToKpi = computed(() =>
  accountScopedRoute(
    'qa_dashboard_agent_kpi',
    {},
    { from: filters.window_start, to: filters.window_end }
  )
);

const liveMonitorRoute = computed(() =>
  accountScopedRoute(
    'qa_monitor_incidents',
    {},
    {
      source_type: topic.value.sourceType,
      tab: activeTab.value,
      from: filters.window_start,
      to: filters.window_end,
      ...(filters.inbox_id ? { inbox_id: filters.inbox_id } : {}),
      ...(filters.team_id ? { team_id: filters.team_id } : {}),
    }
  )
);

const conversationRoute = row =>
  row.conversation_display_id
    ? accountScopedRoute('inbox_conversation', {
        conversation_id: row.conversation_display_id,
      })
    : null;

const listParams = () => ({
  source_type: topic.value.sourceType,
  tab: activeTab.value,
  from: filters.window_start,
  to: filters.window_end,
  page: page.value,
  per_page: PER_PAGE,
  ...(filters.inbox_id ? { inbox_id: filters.inbox_id } : {}),
  ...(filters.team_id ? { team_id: filters.team_id } : {}),
  ...(filters.agent_user_id ? { agent_user_id: filters.agent_user_id } : {}),
});

const episodeParams = () => ({
  window_start: filters.window_start,
  window_end: filters.window_end,
  tab: activeTab.value,
  page: page.value,
  per_page: PER_PAGE,
  ...(filters.inbox_id ? { inbox_id: filters.inbox_id } : {}),
  ...(filters.team_id ? { team_id: filters.team_id } : {}),
  ...(filters.agent_user_id ? { agent_user_id: filters.agent_user_id } : {}),
});

const kpiParams = (overrides = {}) => ({
  window_start: filters.window_start,
  window_end: filters.window_end,
  ...(filters.team_id ? { team_id: filters.team_id } : {}),
  ...(filters.inbox_id ? { inbox_id: filters.inbox_id } : {}),
  ...(filters.agent_user_id ? { agent_user_id: filters.agent_user_id } : {}),
  ...overrides,
});

// Silent on failure: a missing comparison drops the delta, it never blocks.
const loadComparison = async () => {
  previousPayload.value = null;
  try {
    const span = windowDays.value;
    const end = new Date(`${filters.window_start}T00:00:00`).getTime() - DAY_MS;
    const start = end - (span - 1) * DAY_MS;
    const response = await QaDashboardApi.getAgentKpis(
      kpiParams({
        window_start: toDateInput(start),
        window_end: toDateInput(end),
      })
    );
    previousPayload.value = response?.data?.payload || null;
  } catch (error) {
    previousPayload.value = null;
  }
};

const loadHeadline = async () => {
  const response = await QaDashboardApi.getAgentKpis(kpiParams());
  payload.value = response?.data?.payload || {};
  loadComparison();
};

const loadRows = async () => {
  const response =
    topic.value.source === 'episodes'
      ? await QaDashboardApi.getAgentKpiEpisodes(episodeParams())
      : await QaDashboardApi.getIncidents(listParams());
  const data = response?.data || {};
  rows.value = Array.isArray(data.items) ? data.items : [];
  // The two endpoints name this differently: the incident queue has always
  // returned meta.tab_counts, the episodes endpoint returns meta.stats. Reading
  // only one of them left every count on this page as a dash.
  Object.assign(stats, data.meta?.stats || data.meta?.tab_counts || {});
  totalPages.value = data.meta?.total_pages || 1;
  totalCount.value = data.meta?.total_count || 0;
};

const load = async () => {
  isLoading.value = true;
  try {
    await Promise.all([loadHeadline(), loadRows()]);
  } catch (error) {
    showAlert(
      error.response?.data?.error || t('QA_DASHBOARD.AGENT_KPI.ERROR_LOAD')
    );
  } finally {
    isLoading.value = false;
  }
};

const applyWindow = ({ start, end }) => {
  if (!start || !end) return;
  filters.window_start = start;
  filters.window_end = end;
  page.value = 1;
  load();
};

const selectTab = key => {
  activeTab.value = key;
  page.value = 1;
  loadRows();
};

const goPage = next => {
  if (next < 1 || next > totalPages.value) return;
  page.value = next;
  loadRows();
};

const readQuery = () => {
  const query = route.query || {};
  if (query.from) filters.window_start = String(query.from);
  if (query.to) filters.window_end = String(query.to);
  if (query.team_id) filters.team_id = String(query.team_id);
  if (query.inbox_id) filters.inbox_id = String(query.inbox_id);
  if (query.agent_user_id) filters.agent_user_id = String(query.agent_user_id);
};

watch(
  () => route.meta?.qaTopic,
  () => {
    page.value = 1;
    activeTab.value = 'all';
    load();
  }
);

onMounted(async () => {
  readQuery();
  if (!agents.value.length) {
    try {
      await store.dispatch('agents/get');
    } catch (error) {
      // non-fatal: rows fall back to agent ids
    }
  }
  await load();
});

const minDateInput = computed(() =>
  toDateInput(Date.now() - MAX_WINDOW_DAYS * DAY_MS)
);
const todayInput = computed(() => toDateInput(Date.now()));
</script>

<template>
  <div class="qa-page" data-qa-marker="qa-metric-detail-v1">
    <div class="qa-shell">
      <div class="qa-header">
        <div class="qa-header-left">
          <p class="qa-breadcrumb">
            <router-link class="qa-breadcrumb-link" :to="backToKpi">
              ‹ {{ t('QA_DASHBOARD.AGENT_KPI.TITLE') }}
            </router-link>
          </p>
          <h1 class="qa-title">{{ t(topic.titleKey) }}</h1>
          <p class="qa-desc">{{ t(topic.introKey) }}</p>
        </div>
        <div class="qa-header-actions">
          <QaDateRangeSelect
            :start="filters.window_start"
            :end="filters.window_end"
            :min-date="minDateInput"
            :max-date="todayInput"
            @change="applyWindow"
          />
          <router-link
            v-if="topic.source === 'incidents'"
            class="qa-btn"
            :to="liveMonitorRoute"
          >
            {{ t('QA_DASHBOARD.METRIC_DETAIL.OPEN_IN_MONITOR') }}
          </router-link>
        </div>
      </div>

      <div class="qa-content">
        <section class="qa-card" data-qa-headline>
          <div class="qa-card-body qa-detail-headline">
            <div class="qa-detail-figure">
              <span class="qa-label">{{ t(topic.titleKey) }}</span>
              <template v-if="isScored && isCountTopic">
                <strong class="qa-detail-value">{{ num(countValue) }}</strong>
                <span class="qa-basis">
                  {{
                    t('QA_DASHBOARD.METRIC_DETAIL.BASIS_PENDING', {
                      count: num(topic.pending(payload || {})),
                    })
                  }}
                </span>
              </template>
              <template v-else-if="isScored">
                <strong class="qa-detail-value">{{ pct(rate) }}</strong>
                <span class="qa-basis">
                  {{
                    t(topic.basisKey, {
                      correct: num(numerator),
                      total: num(denominator),
                    })
                  }}
                </span>
              </template>
              <template v-else>
                <strong class="qa-detail-value qa-detail-value--muted">
                  {{ t('QA_DASHBOARD.AGENT_KPI.NOT_SCORED') }}
                </strong>
                <span class="qa-basis">
                  {{ t('QA_DASHBOARD.AGENT_KPI.TEMPLATE_NOT_SCORED_BODY') }}
                </span>
              </template>
            </div>
            <div v-if="delta" class="qa-detail-figure">
              <span class="qa-label">
                {{
                  t('QA_DASHBOARD.AGENT_KPI.COMPARE_NOTE', {
                    days: windowDays,
                  })
                }}
              </span>
              <strong :class="deltaClass">{{ deltaText }}</strong>
            </div>
            <div class="qa-detail-steps">
              <div
                v-for="step in breakdown"
                :key="step.key"
                class="qa-detail-step"
              >
                <span class="qa-label">{{ t(step.labelKey) }}</span>
                <strong>{{ step.value }}</strong>
              </div>
            </div>
          </div>
        </section>

        <section class="qa-card">
          <div class="qa-card-header qa-table-header">
            <h2 class="qa-card-title">
              {{ t('QA_DASHBOARD.METRIC_DETAIL.CASES') }}
            </h2>
            <span class="qa-hint">
              {{
                t('QA_DASHBOARD.METRIC_DETAIL.CASES_COUNT', {
                  count: totalCount,
                })
              }}
            </span>
            <span class="qa-header-spacer" />
            <span class="qa-preset" role="group">
              <button
                v-for="tab in TABS"
                :key="tab.key"
                type="button"
                class="qa-preset-btn"
                :class="{ 'qa-preset-btn--on': activeTab === tab.key }"
                :aria-pressed="activeTab === tab.key"
                @click="selectTab(tab.key)"
              >
                {{ t(tab.labelKey) }}
                <span class="qa-basis">{{ num(tab.count) }}</span>
              </button>
            </span>
          </div>
          <div
            v-if="topic.source === 'episodes'"
            class="qa-card-body"
            style="overflow-x: auto"
          >
            <table class="qa-table qa-table--rich">
              <thead>
                <tr>
                  <th>
                    {{ t('QA_DASHBOARD.METRIC_DETAIL.COL_CONVERSATION') }}
                  </th>
                  <th>{{ t('QA_DASHBOARD.AGENT_KPI.COL_STAFF') }}</th>
                  <th class="qa-num">
                    {{ t('QA_DASHBOARD.METRIC_DETAIL.COL_WAITED') }}
                  </th>
                  <th>{{ t('QA_DASHBOARD.METRIC_DETAIL.COL_VERDICT') }}</th>
                  <th>{{ t('QA_DASHBOARD.METRIC_DETAIL.COL_REACHED') }}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr v-if="!rows.length">
                  <td colspan="6" class="qa-empty">
                    {{ t('QA_DASHBOARD.METRIC_DETAIL.EMPTY') }}
                  </td>
                </tr>
                <tr v-for="row in rows" :key="row.id">
                  <td class="qa-mono">
                    #{{ row.conversation_display_id || '—' }}
                  </td>
                  <td>
                    <span class="qa-cell-person">
                      <Avatar
                        :src="agentThumbnail(row.agent_user_id)"
                        :name="agentName(row.agent_user_id)"
                        :size="26"
                        rounded-full
                        hide-offline-status
                      />
                      <span>{{ agentName(row.agent_user_id) }}</span>
                    </span>
                  </td>
                  <td class="qa-num qa-mono">
                    {{ fmtSeconds(row.waited_seconds) }}
                    <span class="qa-basis">/ {{ row.threshold_seconds }}s</span>
                  </td>
                  <td>
                    <span class="qa-pill" :class="verdictTone(row)">
                      {{ t(verdictLabelKey(row)) }}
                    </span>
                  </td>
                  <td class="qa-mono">{{ formatTime(row.claimed_at) }}</td>
                  <td>
                    <router-link
                      v-if="conversationRoute(row)"
                      class="qa-btn qa-btn--sm"
                      :to="conversationRoute(row)"
                    >
                      {{ t('QA_DASHBOARD.METRIC_DETAIL.OPEN_CONVERSATION') }}
                    </router-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="qa-card-body" style="overflow-x: auto">
            <table class="qa-table qa-table--rich">
              <thead>
                <tr>
                  <th>
                    {{ t('QA_DASHBOARD.METRIC_DETAIL.COL_CONVERSATION') }}
                  </th>
                  <th>{{ t('QA_DASHBOARD.AGENT_KPI.COL_STAFF') }}</th>
                  <th>{{ t('QA_DASHBOARD.METRIC_DETAIL.COL_WHAT') }}</th>
                  <th>{{ t('QA_DASHBOARD.METRIC_DETAIL.COL_REVIEW') }}</th>
                  <th>{{ t('QA_DASHBOARD.METRIC_DETAIL.COL_DETECTED') }}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr v-if="!rows.length">
                  <td colspan="6" class="qa-empty">
                    {{ t('QA_DASHBOARD.METRIC_DETAIL.EMPTY') }}
                  </td>
                </tr>
                <tr v-for="row in rows" :key="row.id">
                  <td class="qa-mono">
                    #{{ row.conversation_display_id || '—' }}
                  </td>
                  <td>
                    <span class="qa-cell-person">
                      <Avatar
                        :src="agentThumbnail(row.agent_user_id)"
                        :name="agentName(row.agent_user_id)"
                        :size="26"
                        rounded-full
                        hide-offline-status
                      />
                      <span>{{ agentName(row.agent_user_id) }}</span>
                    </span>
                  </td>
                  <td>
                    <span class="qa-cell-stack">
                      <strong>{{ row.title }}</strong>
                      <span class="qa-kpi-sub">{{ row.summary }}</span>
                    </span>
                  </td>
                  <td>
                    <span
                      class="qa-pill"
                      :class="
                        row.review_state === 'confirmed'
                          ? 'qa-pill--bad'
                          : row.review_state === 'open'
                            ? 'qa-pill--warn'
                            : 'qa-pill--muted'
                      "
                    >
                      {{ row.review_state }}
                    </span>
                  </td>
                  <td class="qa-mono">{{ formatTime(row.detected_at) }}</td>
                  <td>
                    <router-link
                      v-if="conversationRoute(row)"
                      class="qa-btn qa-btn--sm"
                      :to="conversationRoute(row)"
                    >
                      {{ t('QA_DASHBOARD.METRIC_DETAIL.OPEN_CONVERSATION') }}
                    </router-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="totalPages > 1" class="qa-card-footer">
            <button
              type="button"
              class="qa-btn qa-btn--sm"
              :disabled="page <= 1"
              @click="goPage(page - 1)"
            >
              ‹
            </button>
            <span class="qa-legend">{{ page }} / {{ totalPages }}</span>
            <button
              type="button"
              class="qa-btn qa-btn--sm"
              :disabled="page >= totalPages"
              @click="goPage(page + 1)"
            >
              ›
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
.qa-breadcrumb {
  margin: 0 0 4px;
  font-size: 12.5px;
}
.qa-breadcrumb-link {
  color: var(--qa-accent);
  text-decoration: none;
  font-weight: 600;
}
.qa-breadcrumb-link:hover {
  text-decoration: underline;
}
.qa-detail-headline {
  display: flex;
  flex-wrap: wrap;
  gap: 20px 32px;
  align-items: flex-end;
}
.qa-detail-figure {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.qa-detail-value {
  font-size: 38px;
  line-height: 1;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
}
.qa-detail-value--muted {
  font-size: 22px;
  color: var(--qa-text-muted);
}
.qa-detail-steps {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-left: auto;
}
.qa-detail-step {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 108px;
  padding: 8px 12px;
  border: 1px solid var(--qa-border);
  border-radius: 8px;
  background: var(--qa-muted);
}
.qa-detail-step strong {
  font-size: 18px;
  font-variant-numeric: tabular-nums;
}
.qa-kpi-sub {
  display: block;
  font-size: 11px;
  color: var(--qa-text-muted);
}
.qa-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 46ch;
}
.qa-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  white-space: nowrap;
}
.qa-empty {
  text-align: center;
  color: var(--qa-text-muted);
  padding: 24px;
}
</style>
